import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, AlertTriangle, Shield, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuoteView = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadQuote();
  }, [token]);

  const loadQuote = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/token/${token}`);
      setQuote(response.data.quote);
      setApplication(response.data.application);
    } catch (error) {
      if (error.response?.status === 410) {
        setError('This quote has expired.');
      } else {
        setError('Quote not found or invalid link.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/quotes/token/${token}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PlayersHealth_Quote.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download PDF. Please try again.');
      console.error('PDF download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4CD6BE]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Quote Unavailable</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const validUntil = new Date(quote.valid_until);
  const daysRemaining = Math.ceil((validUntil - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Your Insurance Quote | Players Health</title>
        <meta name="description" content="View your personalized insurance quote from Players Health. Coverage tailored for sports organizations and fitness studios." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <img 
            src="/logo-dark.png" 
            alt="Players Health" 
            className="h-10"
          />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Success Banner */}
        <div className="bg-white border-l-4 border-[#4CD6BE] rounded-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="h-8 w-8 text-[#4CD6BE] flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Your Insurance Quote is Ready!</h1>
              <p className="text-slate-600">
                Thank you for your application, {application.applicant_name}. We've prepared a customized quote for {application.org_name}.
              </p>
            </div>
          </div>
        </div>

        {/* Quote Valid Banner */}
        {daysRemaining > 0 && daysRemaining <= 7 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <Shield className="inline h-4 w-4 mr-1" />
              This quote expires in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}. Contact us to proceed.
            </p>
          </div>
        )}

        {/* Quote Details */}
        <Card className="mb-6">
          <CardHeader className="bg-slate-900 text-white rounded-t-xl">
            <CardTitle className="text-2xl">Quote Summary</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-baseline justify-between mb-6 pb-6 border-b border-slate-200">
              <div>
                <p className="text-sm text-slate-600 mb-1">Annual Premium</p>
                <p className="text-4xl font-bold text-slate-900" data-testid="quote-premium">
                  ${quote.premium_amount.toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Valid Until</p>
                <p className="text-lg font-semibold text-slate-900">
                  {validUntil.toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Download PDF Button */}
            <div className="mb-6 pb-6 border-b border-slate-200">
              <Button
                data-testid="download-quote-pdf-btn"
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="w-full bg-[#4CD6BE] hover:bg-[#3BC5AD] text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                {downloading ? 'Downloading...' : 'Download Quote as PDF'}
              </Button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Coverage Included</h3>
              
              {quote.coverage_details.gl_coverage && (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#4CD6BE] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">General Liability</p>
                    <p className="text-sm text-slate-600">Coverage limit: {quote.coverage_details.gl_limit}</p>
                  </div>
                </div>
              )}

              {quote.coverage_details.accident_coverage && (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#4CD6BE] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Accident & Health Coverage</p>
                    <p className="text-sm text-slate-600">
                      Type: {quote.coverage_details.accident_type} coverage
                    </p>
                  </div>
                </div>
              )}

              {quote.coverage_details.optional_coverages?.length > 0 && (
                <div>
                  <p className="font-medium text-slate-900 mb-2">Additional Coverages:</p>
                  <ul className="space-y-2">
                    {quote.coverage_details.optional_coverages.map((coverage, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-[#4CD6BE]" />
                        {coverage.replace('_', ' ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Organization Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Organization Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-slate-600">Organization</dt>
                <dd className="text-slate-900 font-medium">{application.org_name}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-600">Type</dt>
                <dd className="text-slate-900 font-medium">{application.org_type?.replace('_', ' ')}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-600">Number of Participants</dt>
                <dd className="text-slate-900 font-medium">{application.num_participants}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-600">Coverage Start Date</dt>
                <dd className="text-slate-900 font-medium">{application.coverage_start_date}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card>
          <CardHeader>
            <CardTitle>Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {quote.terms}
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-2">Ready to proceed with this coverage?</p>
          <p className="text-lg font-semibold text-slate-900">Contact us at support@playershealth.com</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-600">
          <p>© 2025 Players Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default QuoteView;
